import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Card, CardBody, Col, Container, FormGroup, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap"
import { MetaTags } from "react-meta-tags"
import ToolkitProvider from "react-bootstrap-table2-toolkit"
import BootstrapTable from "react-bootstrap-table-next"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom/cjs/react-router-dom.min"
import { AppConfiguration } from "config"
import SfLoadingSkeleton from "components/Common/Loadings/SfLoadingSkeleton"
import OffsetPagination from "components/Common/OffsetPagination"
import CustomProspectFilters from "components/Common/CustomFilters"
import ShowAppliedFilters from "components/Common/CustomFilters/ShowAppliedFilters"
import CustomExportCSVButton from "components/Common/CustomExportCSVButton"
import { customSortCaret } from "helpers/bootstrap_table_helper"
import { copyToClipboard } from "helpers/Integrations/req_res_text_area_helper"
import { exportAdminBulkEditJobs, getAdminBulkEditJobs } from "store/admin/admin-bulk-edit-jobs/actions"
import { postAdminUserLogin } from "store/admin/user-login/actions"
import { BULK_EDIT_JOBS_FILTER_OBJECTS, bulkEditJobsColumns, mapBulkEditJobsFilters, triggeredByFormatter } from "./admin_bulk_edit_jobs_helper"
import { dateFormatter } from "helpers/sf_analytics_helper"
import { getActiveFilterItems, onClickOpenFilterModal, onClickRemoveFilter, onCloseFilterModal } from "./AdminBulkEditJobsFilterUtils"
import { getPrimaryButtonClass, getPrimaryTextClass } from "helpers/sf_utils"
import ReqResTextarea from "components/integrations/ReqResTextarea"

const DEFAULT_SORT_COLUMN = "created_at"
const DEFAULT_SORT_DIRECTION = "desc"
// The paired API endpoint (POST /api/v1/admin_bulk_edit_jobs/export) only
// counts matching rows today -- it doesn't create an export log, enqueue a
// job, or generate a file, so this button would otherwise show a false
// "queued" success. Flip this back on once that backend work lands.
// Stable reference: a new object here would make BootstrapTable think its
// remote config changed on every render and re-fire onTableChange, looping.
const REMOTE_SORT_CONFIG = { sort: true }
const DEFAULT_FILTER_ITEMS = Object.values(BULK_EDIT_JOBS_FILTER_OBJECTS)
const EMPTY_MODAL_STATE = { isOpen: false, type: null, title: "", job: null, content: "" }

const formatModalValue = (value) => {
    if (value === null || value === undefined || value === "") return "--"
    if (typeof value === "string") return value
    return JSON.stringify(value, null, 2)
}

const JOB_DETAIL_TABS = { INFORMATION: "dev information", PAYLOAD: "payload" }
const MESSAGE_DETAIL_TABS = { ERROR: "error", REQUEST: "request" }
const ERROR_MESSAGE_TITLE = "Error Message"

const JOB_DETAIL_FIELDS = [
    { key: "job_name", text: "Name" },
    { key: "jid", text: "Job ID" },
    { key: "request_id", text: "Request ID" },
]

const detailsModalIcon = (detailsModal) => {
    if (detailsModal.type === "job") return `bx-detail ${getPrimaryTextClass()}`
    if (detailsModal.title === ERROR_MESSAGE_TITLE) return "bx-error-circle text-danger"
    return `bx-message-square-detail ${getPrimaryTextClass()}`
}

const DetailsTabs = ({ tabs, activeTab, onSelectTab, showBeautify, isBeautify, onToggleBeautify }) => (
    <ul className="nav nav-tabs justify-content-between">
        <div className="d-flex">
            {tabs.map((tab) => (
                <li className="nav-item" key={tab}>
                    <a
                        className={`nav-link text-black hover-none text-capitalize ${activeTab === tab ? "active" : ""}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => onSelectTab(tab)}
                    >
                        {tab}
                    </a>
                </li>
            ))}
        </div>
        {showBeautify && (
            <li className="nav-item d-flex align-items-center form-check form-switch form-switch-md">
                <label htmlFor="bulk-edit-details-beautify" className="font-weight-medium mb-0 me-2">Beautify</label>
                <input
                    id="bulk-edit-details-beautify"
                    type="checkbox"
                    className="form-check-input ms-3 my-auto"
                    checked={isBeautify}
                    onChange={onToggleBeautify}
                />
            </li>
        )}
    </ul>
)

const PayloadView = ({ payload, isBeautify, emptyText }) => (
    formatModalValue(payload) === "--"
        ? <div className="text-muted">{emptyText}</div>
        : <ReqResTextarea key="payload" isBeautify={isBeautify} val={formatModalValue(payload)} />
)

// Errors come back as JSON objects joined with ", " (one per failure), which isn't valid JSON
// on its own once there's more than one; read them as a list so they can be beautified.
const parseErrorMessage = (content) => {
    if (typeof content !== "string" || !content.trim()) return null
    const text = content.trim()
    if (!text.startsWith("{") && !text.startsWith("[")) return null
    for (const candidate of [text, `[${text}]`]) {
        try {
            return JSON.stringify(JSON.parse(candidate))
        } catch (e) {
            // not JSON in this shape; try the next one
        }
    }
    return null
}

const ErrorView = ({ content, isBeautify }) => {
    if (formatModalValue(content) === "--") return <div className="text-muted">No error message.</div>
    const json = parseErrorMessage(content)
    return json
        ? <ReqResTextarea key="error" isBeautify={isBeautify} val={json} />
        : <ReqResTextarea key="error" isBeautify={false} val={formatModalValue(content)} />
}

const TriggeredBy = ({ job }) => (
    <div className="bg-light border rounded p-3 mb-3 text-black d-flex align-items-center flex-wrap">
        <i className={`bx bx-user font-size-16 ${getPrimaryTextClass()} me-2`}></i>
        <span>
            Triggered by <strong>{triggeredByFormatter(job?.triggered_by_display_name, job || {})}</strong>
            {job?.created_at && (
                <>
                    {" on "}
                    <i className={`bx bx-calendar font-size-16 ${getPrimaryTextClass()} mx-1 align-middle`}></i>
                    <strong>{dateFormatter(job.created_at)} UTC</strong>
                </>
            )}
            {job?.account_name && (
                <> for <strong>{job.account_name}</strong></>
            )}
            .
        </span>
    </div>
)

const ReasonView = ({ content }) => (
    formatModalValue(content) === "--"
        ? <div className="text-muted">No reason given.</div>
        : <ReqResTextarea key="reason" isBeautify={false} val={formatModalValue(content)} />
)

const AdminBulkMigration = () => {
    const dispatch = useDispatch()
    const history = useHistory()

    const { bulk_edit_jobs, loading, error, page, total_pages, exporting } = useSelector((state) => ({
        bulk_edit_jobs: state.AdminBulkEditJobs.bulk_edit_jobs,
        loading: state.AdminBulkEditJobs.loading,
        error: state.AdminBulkEditJobs.error,
        page: state.AdminBulkEditJobs.page,
        total_pages: state.AdminBulkEditJobs.total_pages,
        exporting: state.AdminBulkEditJobs.exporting,
    }))

    const [searchValue, setSearchValue] = useState("")
    const [isActiveFilterModal, setIsActiveFilterModal] = useState(false)
    const [filterItems, setFilterItems] = useState(DEFAULT_FILTER_ITEMS)
    const [detailsModal, setDetailsModal] = useState(EMPTY_MODAL_STATE)
    const [isBeautify, setIsBeautify] = useState(true)
    const [detailsTab, setDetailsTab] = useState(JOB_DETAIL_TABS.INFORMATION)
    const [sortColumn, setSortColumn] = useState(DEFAULT_SORT_COLUMN)
    const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION)

    const { filterCount, activeFilterItems } = useMemo(() => getActiveFilterItems(filterItems), [filterItems])

    const filtersPayload = useMemo(() => {
        const filters = mapBulkEditJobsFilters(activeFilterItems)
        if (searchValue) filters.search = searchValue
        return filters
    }, [activeFilterItems, searchValue])

    const fetchBulkEditJobsPage = useCallback((pageNumber = 1, filters = filtersPayload, sortCol = sortColumn, sortDir = sortDirection) => {
        const queryParams = { page: pageNumber, sort_column: sortCol, sort_direction: sortDir }
        if (filters && Object.keys(filters).length) {
            queryParams.filters = JSON.stringify(filters)
        }
        dispatch(getAdminBulkEditJobs({ queryParams }))
    }, [dispatch, filtersPayload, sortColumn, sortDirection])

    useEffect(() => {
        // Intentionally mount-only: fetches with the initial default filters/sort.
        // Every later filter/sort/search change calls fetchBulkEditJobsPage with its
        // own explicit args (see below), so this effect never needs to re-fire or
        // depend on fetchBulkEditJobsPage's identity.
        fetchBulkEditJobsPage(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSearch = (value) => {
        setSearchValue(value)
        const filters = mapBulkEditJobsFilters(activeFilterItems)
        if (value) filters.search = value
        fetchBulkEditJobsPage(1, filters)
    }

    const onSubmitFilterModal = (values) => {
        setFilterItems(values)
        const { activeFilterItems: submittedActiveItems } = getActiveFilterItems(values)
        const filters = mapBulkEditJobsFilters(submittedActiveItems)
        if (searchValue) filters.search = searchValue
        fetchBulkEditJobsPage(1, filters)
        onCloseFilterModal(setIsActiveFilterModal)
    }

    const onRemoveFilter = (fieldName) => {
        onClickRemoveFilter(fieldName, setFilterItems, (updatedActiveItems) => {
            const filters = mapBulkEditJobsFilters(updatedActiveItems)
            if (searchValue) filters.search = searchValue
            fetchBulkEditJobsPage(1, filters)
        })
    }

    const onTableChange = (type, { sortField, sortOrder }) => {
        if (type !== "sort") {
            return
        }
        // The table remounts behind the loading skeleton on every fetch and
        // re-fires onTableChange reporting the current sort; ignore that
        // redundant call so it doesn't trigger another fetch (infinite loop).
        if (sortField === sortColumn && sortOrder === sortDirection) {
            return
        }
        setSortColumn(sortField)
        setSortDirection(sortOrder)
        fetchBulkEditJobsPage(1, filtersPayload, sortField, sortOrder)
    }

    const openJobDetailsModal = useCallback((job) => {
        setDetailsTab(JOB_DETAIL_TABS.INFORMATION)
        setDetailsModal({ isOpen: true, type: "job", title: "Job Details", job, content: "" })
    }, [])

    const openMessageModal = useCallback((title, content, job = null) => {
        setDetailsTab(MESSAGE_DETAIL_TABS.ERROR)
        setDetailsModal({ isOpen: true, type: "message", title, job, content })
    }, [])

    const onClickAccountLogin = useCallback((accountId) => {
        if (!accountId) return
        dispatch(postAdminUserLogin({ user: { account: accountId }, history }))
    }, [dispatch, history])

    const renderMessageBox = (content) => (
        <div>
            <div className="d-flex align-items-center justify-content-end mb-1">
                <button
                    type="button"
                    className="text-sfblue bg-transparent border-0 p-0"
                    onClick={() => copyToClipboard(formatModalValue(content))}
                >
                    <i className="bx bx-copy me-1" />
                    Copy
                </button>
            </div>
            <div className="bg-light border rounded p-3 text-black" style={{ maxHeight: "240px", overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {formatModalValue(content)}
            </div>
        </div>
    )

    const closeDetailsModal = () => setDetailsModal(EMPTY_MODAL_STATE)

    const columns = useMemo(() => bulkEditJobsColumns({ openJobDetailsModal, openMessageModal, onClickAccountLogin }), [openJobDetailsModal, openMessageModal, onClickAccountLogin])

    const onClickExport = () => {
        if (exporting) return
        const queryParams = { sort_column: sortColumn, sort_direction: sortDirection }
        if (filtersPayload && Object.keys(filtersPayload).length) {
            queryParams.filters = JSON.stringify(filtersPayload)
        }
        dispatch(exportAdminBulkEditJobs({ queryParams }))
    }

    return (
        <React.Fragment>
            <div className="page-content" id="page-content-wrapper">
                <MetaTags>
                    <title>Bulk Edit Logs | {AppConfiguration.WEBSITE}</title>
                </MetaTags>
                <CustomProspectFilters
                    isActive={isActiveFilterModal}
                    filterItems={filterItems}
                    onSubmitModalState={onSubmitFilterModal}
                    onClickModalClose={() => onCloseFilterModal(setIsActiveFilterModal)}
                    isActiveSearchBox={false}
                />
                <Modal isOpen={detailsModal.isOpen} toggle={closeDetailsModal} size="lg" centered={true}>
                    <ModalHeader>
                        <div className="d-flex align-items-center">
                            <i className={`label-title bx ${detailsModalIcon(detailsModal)} lh-1 me-2`}></i>
                            <span className="modal-title call-transcription-topic-heading lh-1">{detailsModal.title}</span>
                        </div>
                        <button type="button" className="close" aria-label="Close" onClick={closeDetailsModal}>
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </ModalHeader>
                    <ModalBody className="bulk-edit-modal-body" style={{ overflowY: "auto" }}>
                        {detailsModal.type === "job" ? (
                            <>
                                <DetailsTabs
                                    tabs={Object.values(JOB_DETAIL_TABS)}
                                    activeTab={detailsTab}
                                    onSelectTab={setDetailsTab}
                                    showBeautify={detailsTab === JOB_DETAIL_TABS.PAYLOAD}
                                    isBeautify={isBeautify}
                                    onToggleBeautify={() => setIsBeautify(!isBeautify)}
                                />
                                <div className="pt-3">
                                    {detailsTab === JOB_DETAIL_TABS.PAYLOAD ? (
                                        <PayloadView payload={detailsModal.job?.payload} isBeautify={isBeautify} emptyText="No payload." />
                                    ) : (
                                        <table className="table mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Key</th>
                                                    <th scope="col">Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {JOB_DETAIL_FIELDS.map(({ key, text }) => (
                                                    <tr key={key} style={{ cursor: "default" }}>
                                                        <td className="text-capitalize font-weight-semibold text-nowrap">{text}</td>
                                                        <td className="text-break">{formatModalValue(detailsModal.job?.[key])}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </>
                        ) : detailsModal.job && detailsModal.title !== ERROR_MESSAGE_TITLE ? (
                            <>
                                <TriggeredBy job={detailsModal.job} />
                                <ReasonView content={detailsModal.content} />
                            </>
                        ) : detailsModal.job ? (
                            <>
                                <TriggeredBy job={detailsModal.job} />
                                <DetailsTabs
                                    tabs={[MESSAGE_DETAIL_TABS.ERROR, MESSAGE_DETAIL_TABS.REQUEST]}
                                    activeTab={detailsTab}
                                    onSelectTab={setDetailsTab}
                                    showBeautify={true}
                                    isBeautify={isBeautify}
                                    onToggleBeautify={() => setIsBeautify(!isBeautify)}
                                />
                                <div className="pt-3">
                                    {detailsTab === MESSAGE_DETAIL_TABS.REQUEST ? (
                                        <PayloadView payload={detailsModal.job?.payload} isBeautify={isBeautify} emptyText="No request details." />
                                    ) : (
                                        <ErrorView content={detailsModal.content} isBeautify={isBeautify} />
                                    )}
                                </div>
                            </>
                        ) : (
                            renderMessageBox(detailsModal.content)
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <button type="button" className={`btn ${getPrimaryButtonClass()} ms-2`} onClick={closeDetailsModal}>Close</button>
                    </ModalFooter>
                </Modal>
                <Container fluid>
                    <div className="page-title-box pb-2 d-flex align-items-center justify-content-between">
                        <h4 className="text-capitalize text-black mb-0 font-size-18">
                            Bulk Edit Logs
                        </h4>
                    </div>
                    <Row>
                        <Col md={12}>
                            <Card>
                                <CardBody>
                                    <Row className="mb-2 d-flex align-items-center">
                                        <Col md="auto" className="d-flex align-items-center">
                                            <FormGroup className="mb-0">
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="d-flex align-items-center btn btn-outline-primary"
                                                        onClick={() => onClickOpenFilterModal(setIsActiveFilterModal, setFilterItems, BULK_EDIT_JOBS_FILTER_OBJECTS)}
                                                    >
                                                        <i className="bx bx-filter font-size-18 align-middle me-2" />
                                                        Filters {filterCount !== 0 && <span className="numberCircle border-0"> {filterCount} </span>}
                                                    </button>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                        <Col md="2" className="d-flex align-items-center">
                                            <FormGroup className="mb-0 w-100">
                                                <div className="search-box d-flex align-items-center w-100">
                                                    <div className="position-relative w-100">
                                                        <Input
                                                            className="rounded"
                                                            placeholder="Search job name, job ID, request ID, account, user or status"
                                                            title="Search by job name, job ID, request ID, account (ID or name), triggered by (ID, name or email) or status"
                                                            value={searchValue}
                                                            onChange={(e) => {
                                                                setSearchValue(e.target.value)
                                                                if (e.target.value === "") {
                                                                    onSearch("")
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault()
                                                                    onSearch(e.target.value)
                                                                }
                                                            }}
                                                        />
                                                        <i className="bx bx-search-alt search-icon" />
                                                    </div>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                        <Col className="flex-nowrap d-flex align-items-center" style={{ maxHeight: "100px" }}>
                                            <ShowAppliedFilters
                                                filtered_columns={activeFilterItems}
                                                onClickRemoveFilter={onRemoveFilter}
                                            />
                                        </Col>
                                        <Col md="auto" className="d-flex gap-2 ms-auto">
                                            <CustomExportCSVButton
                                                className={`d-flex align-items-center btn btn-outline-primary${exporting ? " disabled" : ""}`}
                                                applyCustomOnClick
                                                onClick={onClickExport}
                                            >
                                                <i className="bx bx-download font-size-18 me-2" />
                                                {exporting ? "Exporting..." : "Export"}
                                            </CustomExportCSVButton>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl="12">
                                            <div className="sfTable-Container">
                                                {error ? (
                                                    <div className="text-center text-danger py-4">
                                                        Something went wrong while loading Bulk Edit Logs. Please try again.
                                                    </div>
                                                ) : loading ? (
                                                    <SfLoadingSkeleton rows={8} cols={10} />
                                                ) : (
                                                    <ToolkitProvider
                                                        keyField="id"
                                                        columns={columns}
                                                        data={bulk_edit_jobs || []}
                                                    >
                                                        {(toolkitProps) => (
                                                            <BootstrapTable
                                                                keyField="id"
                                                                responsive
                                                                bordered={false}
                                                                striped={false}
                                                                rowClasses="tr-hover"
                                                                headerClasses="sftableHeader text-black thead-h50"
                                                                classes="table align-middle table-nowrap text-black"
                                                                headerWrapperClasses="thead-light"
                                                                noDataIndication="You currently don't have any bulk edit logs"
                                                                defaultSorted={[{ dataField: sortColumn, order: sortDirection }]}
                                                                sort={{ sortCaret: customSortCaret }}
                                                                remote={REMOTE_SORT_CONFIG}
                                                                onTableChange={onTableChange}
                                                                {...toolkitProps.baseProps}
                                                            />
                                                        )}
                                                    </ToolkitProvider>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                    <Col xl="12">
                                        <OffsetPagination
                                            currentPage={page}
                                            totalPages={total_pages}
                                            hasNextPage={page < total_pages}
                                            hasDeepOffsetPages={false}
                                            loading={loading}
                                            onPageChange={(pageNumber) => fetchBulkEditJobsPage(pageNumber)}
                                            className="campaign-analysis-pagination mt-3 align-items-center justify-content-end"
                                        />
                                    </Col>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default AdminBulkMigration
